// Date-range state for the dashboard filter.
//
// Ranges are expressed as plain calendar dates (YYYY-MM-DD) in the *browser's*
// timezone, and the zone is sent alongside them. The API stores submission
// timestamps in UTC, so it needs the zone to decide which local day a given
// instant belongs to — otherwise an evening submission in California lands on
// the following day.

export const RANGE_PRESETS = [
  { id: '7', label: '7 days', days: 7 },
  { id: '30', label: '30 days', days: 30 },
  { id: '90', label: '90 days', days: 90 },
];

export const DEFAULT_RANGE = { preset: '7', start: '', end: '' };

const pad = (n) => String(n).padStart(2, '0');

// toISOString() would render the UTC day, which is the previous date for
// anyone west of UTC after 5pm. Build the string from local parts instead.
export const toDateInput = (date) =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const todayInput = () => toDateInput(new Date());

export const daysAgoInput = (days) => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return toDateInput(date);
};

export const browserTimezone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
};

// A custom range only takes effect once both ends are filled in; until then the
// dashboard keeps showing the last preset rather than flickering to a partial
// window as the user types.
export const isCustomRangeReady = (range) =>
  range.preset === 'custom' && Boolean(range.start) && Boolean(range.end);

export const rangeQuery = (range) => {
  const params = new URLSearchParams();
  if (isCustomRangeReady(range)) {
    params.set('start', range.start);
    params.set('end', range.end);
  } else {
    const preset = RANGE_PRESETS.find((p) => p.id === range.preset) || RANGE_PRESETS[0];
    params.set('days', String(preset.days));
  }
  const tz = browserTimezone();
  if (tz) params.set('tz', tz);
  return params.toString();
};

// 'YYYY-MM-DD' -> local Date. new Date('2026-08-09') parses as UTC midnight,
// which reads as Aug 8 in the Americas; splitting the parts avoids that.
export const parseDateInput = (value) => {
  const [year, month, day] = String(value).split('-').map(Number);
  return new Date(year, (month || 1) - 1, day || 1);
};

export const formatDay = (value, options = { month: 'short', day: 'numeric' }) =>
  parseDateInput(value).toLocaleDateString(undefined, options);

export const formatRangeLabel = (start, end) => {
  if (!start || !end) return '';
  const sameYear = start.slice(0, 4) === end.slice(0, 4);
  const startLabel = formatDay(start, { month: 'short', day: 'numeric', ...(sameYear ? {} : { year: 'numeric' }) });
  const endLabel = formatDay(end, { month: 'short', day: 'numeric', year: 'numeric' });
  return `${startLabel} – ${endLabel}`;
};
